import type { OHLCV } from './api-client';

/** A single trading-day cell in a fixed-width window around an event.
 *  Used by both the earnings-around-event grid on /stocks/[ticker] and
 *  the SP500-reaction grid on /economic-calendar (issue #232) — same
 *  shape, same math; only the anchor logic differs.
 *
 *  Naming: `isEventDay` is generic. Earnings-specific callers re-expose
 *  the same boolean as `isEarningsDay` via the wrapper in earningsWindow.ts. */
export interface EventCell {
  /** Daily close-to-close % change (this bar vs prior trading bar). */
  dailyPct: number;
  /** Cumulative drift % since the event-day close (= the bar at D0). */
  driftPct: number;
  /** Closing price for this trading day, in dollars. */
  price: number;
  /** Trading-day offset from the event (0 = event day, +N = N days after). */
  offset: number;
  /** YYYY-MM-DD of the bar this cell came from. */
  date: string;
  /** True for the cell at offset 0 — the anchor bar / event day. */
  isEventDay: boolean;
}

/** Build a fixed-width window of cells around a chosen anchor index.
 *  Shared cell-construction loop used by both buildEventWindow (the
 *  generic past-event flow) and buildEarningsWindow (which adds AMC/BMO
 *  + future-event handling on top of the same primitive). Returns
 *  `before + 1 + after` slots; slots outside the bars range are null.
 *
 *  When `isFutureEvent` is true and the anchor index is past the last
 *  bar, a placeholder cell is synthesized at offset 0 so callers can
 *  still render the star. */
export function buildCellsAroundIndex(
  bars: OHLCV[],
  anchorIdx: number,
  before: number,
  after: number,
  baseClose: number,
  eventDate: string,
  isFutureEvent: boolean,
): (EventCell | null)[] {
  const cells: (EventCell | null)[] = [];
  for (let i = -before; i <= after; i++) {
    const j = anchorIdx + i;
    if (j < 0 || j >= bars.length) {
      if (i === 0 && isFutureEvent) {
        cells.push({ dailyPct: 0, driftPct: 0, price: 0, offset: 0, date: eventDate, isEventDay: true });
      } else {
        cells.push(null);
      }
      continue;
    }
    const bar = bars[j];
    const prevClose = j > 0 ? bars[j - 1].close : NaN;
    const dailyPct = Number.isFinite(prevClose) && prevClose > 0 && bar.close > 0
      ? ((bar.close - prevClose) / prevClose) * 100
      : 0;
    const driftPct = Number.isFinite(baseClose) && baseClose > 0 && bar.close > 0
      ? ((bar.close - baseClose) / baseClose) * 100
      : 0;
    cells.push({
      dailyPct,
      driftPct,
      price: bar.close,
      offset: i,
      date: bar.date,
      isEventDay: i === 0,
    });
  }
  return cells;
}

/** Build a fixed-width window of trading-day cells around a past event.
 *  Generic, calendar-anchored (no AMC/BMO shifting — that's an earnings-
 *  specific concept). For the SP500-reaction tab on /economic-calendar
 *  and other future event-reaction grids.
 *
 *  Anchor = first bar with `date >= eventDate` (so weekend / holiday
 *  events take the next trading day). Returns null slots at the edges
 *  when the bars range doesn't extend that far.
 *
 *  Events with no bar at or after eventDate (i.e. future events) get
 *  an all-null window — for the future-event case use the earnings
 *  variant which synthesizes a D0 placeholder.
 */
export function buildEventWindow(
  bars: OHLCV[],
  eventDate: string,
  before = 5,
  after = 5,
): (EventCell | null)[] {
  const total = before + 1 + after;
  const empty = (): (EventCell | null)[] => Array(total).fill(null);
  if (!Array.isArray(bars) || bars.length === 0 || !eventDate) return empty();
  const idx = bars.findIndex((b) => b.date >= eventDate);
  if (idx === -1) return empty();
  const baseClose = bars[idx].close;
  return buildCellsAroundIndex(bars, idx, before, after, baseClose, eventDate, false);
}
